import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Clear, ExpandLess, ExpandMore } from '@mui/icons-material';
import { captureException } from '@sentry/react';
import { formatDateInEasternWithDateFns } from 'util/dateUtils';
import {
  TransactionReviewMessage,
  TransactionReviewMessageAuthorRole,
  transactionService,
} from 'app/services/transaction.service';
import { sentryMask } from 'sentryReplayPrivacy';

interface Props {
  transactionId: string;
  /** Admins / group admins can post; everyone else gets the read-only thread. */
  canReturn: boolean;
  /** After a return. Resolve true when the sheet closed, so the thread is not reloaded. */
  onReturned?: () => void | boolean | Promise<void | boolean>;
  /** 'sidesheet': desktop collapsible card. 'mobile': flush dark-theme section. */
  variant?: 'sidesheet' | 'mobile';
  /** Optional button rendered beside "Return to cardholder" (e.g. Mark as reviewed). */
  actionSlot?: React.ReactNode;
  /** Optional third button in the same row (e.g. Bypass Posting). */
  secondaryActionSlot?: React.ReactNode;
  /**
   * SEE-1598 extended side sheet: render the Return / action buttons into this
   * element (the sheet's sticky footer) instead of under the history card. The
   * draft and its submit state stay here; only the buttons move.
   */
  actionsContainer?: HTMLElement | null;
}

const roleLabel = (role: TransactionReviewMessageAuthorRole | null | undefined): string => {
  if (role === 'GROUP_ADMIN') return 'Group admin';
  if (role === 'SECONDARY_ADMIN') return 'Secondary admin';
  if (role === 'ADMIN') return 'Admin';
  return '';
};

const formatTimestamp = (iso: string): string => {
  // "ET" (not "EST") since America/New_York is EDT for ~8 months of the year.
  const formatted = formatDateInEasternWithDateFns(iso, 'MM/dd/yyyy, h:mm aaa');
  return formatted ? `${formatted} ET` : iso;
};

const MessageToCardholder = ({
  transactionId,
  canReturn,
  onReturned,
  variant = 'sidesheet',
  actionSlot,
  secondaryActionSlot,
  actionsContainer,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = variant === 'mobile';

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [messages, setMessages] = useState<TransactionReviewMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  // Desktop sidesheet opens expanded; mobile lands collapsed (Figma 6382:298526).
  const [historyOpen, setHistoryOpen] = useState(variant !== 'mobile');
  const [inputFocused, setInputFocused] = useState(false);

  const loadMessages = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!transactionId) return;
      setLoading(true);
      setError(false);
      try {
        setMessages(await transactionService.listTransactionReviewMessages(transactionId));
      } catch (err) {
        captureException(err);
        // A reload right after a successful return must not read as the return failing.
        if (!silent) setError(true);
      } finally {
        setLoading(false);
      }
    },
    [transactionId]
  );

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Reset on transaction switch so a stale draft / prior thread doesn't bleed through.
  useEffect(() => {
    setDraft('');
    setMessages([]);
  }, [transactionId]);

  // Newest first.
  const orderedMessages = useMemo(
    () => [...messages].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [messages]
  );

  const title = canReturn
    ? t('transactionSidesheet.messageToCardholder.history')
    : t('transactionSidesheet.messageToCardholder.fromAdmin');

  const handleReturn = useCallback(async () => {
    const message = draft.trim();
    if (!message) return;
    setSubmitting(true);
    setError(false);
    try {
      await transactionService.returnTransactionToCardholder({ transactionId, message });
      setDraft('');
      // SEE-1605: the sheet goes first and says whether it closed. Returning the last
      // split of a split folds the split back and deletes this row: there is no thread
      // left to reload, and the return already succeeded, so nothing here may fail it.
      const closed = await onReturned?.();
      if (closed || !mountedRef.current) return;
      await loadMessages({ silent: true });
    } catch (err) {
      captureException(err);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }, [draft, transactionId, loadMessages, onReturned]);

  // Cardholders only see the thread when it's non-empty; admins always see it (to post first).
  if (!canReturn && orderedMessages.length === 0) {
    return null;
  }

  const dotColor = isMobile ? 'common.white' : 'primary.main';
  const messageColor = isMobile ? 'common.white' : 'text.primary';
  const subColor = isMobile ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled';

  const historyItems = (
    <Box>
      {orderedMessages.map((m, index) => {
        const isLast = index === orderedMessages.length - 1;
        const author = [m.authorName, roleLabel(m.authorRole)].filter(Boolean).join(', ');
        return (
          <Box key={m.id} sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  mt: '5px',
                  borderRadius: '50%',
                  bgcolor: dotColor,
                  flexShrink: 0,
                }}
              />
              {!isLast && <Box sx={{ flexGrow: 1, width: '2px', my: 0.5, bgcolor: dotColor }} />}
            </Box>
            <Box sx={{ pb: isLast ? 0 : 2 }}>
              <Typography
                variant="body2"
                {...sentryMask}
                sx={{
                  color: messageColor,
                  ...(isMobile && { fontSize: '1.4rem', lineHeight: '20px', letterSpacing: '0.25px' }),
                }}
              >
                {m.message}
              </Typography>
              {author && (
                <Typography
                  display="block"
                  sx={{ fontSize: isMobile ? '1.1rem' : '1rem', color: subColor }}
                  {...sentryMask}
                >
                  {author}
                </Typography>
              )}
              <Typography display="block" sx={{ fontSize: isMobile ? '1.1rem' : '1rem', color: subColor }}>
                {formatTimestamp(m.createdAt)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // Mobile is its own collapsible dark-glass card at the top of the details page
  // (Figma 6382:297621) — same header + chevron + thread as the desktop sidesheet.
  if (isMobile) {
    return (
      <Box sx={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 3, px: 2 }}>
        <Box
          onClick={() => setHistoryOpen((open) => !open)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ color: 'common.white', fontSize: '1.4rem', letterSpacing: '0.25px' }}>
            {title}
          </Typography>
          <IconButton size="small" aria-label={title} sx={{ color: 'common.white', p: 0 }}>
            {historyOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={historyOpen}>
          <Box sx={{ pb: 2 }}>
            {loading ? (
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
            ) : error ? (
              <Typography variant="body2" sx={{ color: 'common.white' }}>
                {t('transactionSidesheet.messageToCardholder.error')}
              </Typography>
            ) : (
              historyItems
            )}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ mb: canReturn ? 0 : 2 }}>
      {canReturn && (
        <Box>
          {!inputFocused && draft.trim() === '' && (
            <Typography sx={{ fontSize: '1rem', color: 'text.secondary', mb: 0.5 }}>
              {t('transactionSidesheet.messageToCardholder.returnReasonRequired')}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={draft}
            label={t('transactionSidesheet.messageToCardholder.label')}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            disabled={submitting}
            inputProps={sentryMask}
            InputProps={{
              endAdornment: draft.trim() !== '' && (
                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  <IconButton size="small" aria-label="clear" onClick={() => setDraft('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {error && <Alert severity="error">{t('transactionSidesheet.messageToCardholder.error')}</Alert>}

      <Box
        sx={{
          border: '1px solid #aec9ff',
          borderRadius: '1rem',
          backgroundColor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Box
          onClick={() => setHistoryOpen((open) => !open)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          <IconButton size="small" aria-label={title}>
            {historyOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={historyOpen}>
          <Box sx={{ px: 2, pb: 2 }}>
            {loading ? (
              <CircularProgress size={20} />
            ) : orderedMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('transactionSidesheet.messageToCardholder.empty')}
              </Typography>
            ) : (
              historyItems
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Return + (optional) Mark-as-reviewed sit at the very bottom, after history,
          or in the sheet's sticky footer when the extended sheet provides one. */}
      {canReturn && (actionsContainer ? createPortal(renderActions(), actionsContainer) : renderActions())}
    </Stack>
  );

  // Equal columns regardless of label length (2 buttons → halves, 3 → thirds).
  function renderActions(): React.ReactNode {
    return (
      <Box
        data-testid="message-to-cardholder-actions"
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: 'minmax(0, 1fr)',
          gap: 1.5,
          width: '100%',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ borderRadius: '8px' }}
          disabled={draft.trim() === '' || submitting}
          onClick={handleReturn}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('transactionSidesheet.messageToCardholder.returnCta')
          )}
        </Button>
        {actionSlot && <Box sx={{ display: 'flex', '& > *': { flex: 1 } }}>{actionSlot}</Box>}
        {secondaryActionSlot && (
          <Box sx={{ display: 'flex', '& > *': { flex: 1 } }}>{secondaryActionSlot}</Box>
        )}
      </Box>
    );
  }
};

export default MessageToCardholder;
